/**
 * Resolve / persist the active Master Account (tenant) id for
 * X-KK-Active-Master-Account-Id on bootstrap and API calls.
 *
 * Storage is scoped to the Auth0 sub so a prior user's MA id cannot
 * poison bootstrap for a different signed-in owner (CROSS_TENANT).
 */

import { loadImpersonationSession } from "../supportMode/supportModeSession";

export const ACTIVE_MA_STORAGE_KEY = "kk_active_master_account_id";

function subFromToken(token) {
  if (!token || typeof token !== "string") return null;
  try {
    const payload = JSON.parse(
      atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")),
    );
    return payload.sub ? String(payload.sub) : null;
  } catch {
    return null;
  }
}

export function persistActiveMasterAccountId(masterAccountId, accessToken) {
  if (!masterAccountId) return;
  const sub = subFromToken(accessToken);
  try {
    if (sub) {
      localStorage.setItem(
        ACTIVE_MA_STORAGE_KEY,
        JSON.stringify({
          sub,
          masterAccountId: String(masterAccountId),
        }),
      );
    } else {
      localStorage.setItem(ACTIVE_MA_STORAGE_KEY, String(masterAccountId));
    }
  } catch {
    /* ignore quota / private mode */
  }
}

export function clearActiveMasterAccountId() {
  try {
    localStorage.removeItem(ACTIVE_MA_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Non-destructive peek of the persisted MA id.
 * Never clears storage — safe for sync helpers (e.g. Channels zoneId)
 * that do not have an access token in scope.
 */
export function peekStoredActiveMasterAccountId() {
  try {
    const raw = localStorage.getItem(ACTIVE_MA_STORAGE_KEY);
    if (!raw || !raw.startsWith("{")) return null;
    const parsed = JSON.parse(raw);
    return parsed?.masterAccountId
      ? String(parsed.masterAccountId)
      : null;
  } catch {
    return null;
  }
}

/**
 * Read persisted active MA id, validating against the Auth0 sub in
 * `accessToken` when provided.
 *
 * IMPORTANT: omitting `accessToken` must NOT clear storage. Channels
 * previously called this without a token for zoneId and wiped the
 * active MA, causing subsequent API calls to omit
 * X-KK-Active-Master-Account-Id.
 */
export function readStoredActiveMasterAccountId(accessToken) {
  try {
    const raw = localStorage.getItem(ACTIVE_MA_STORAGE_KEY);
    if (!raw) return null;

    // Legacy unscoped value (plain MA id) — discard; it caused cross-user
    // bootstrap denials when switching Auth0 owners in the same browser.
    if (!raw.startsWith("{")) {
      clearActiveMasterAccountId();
      return null;
    }

    const parsed = JSON.parse(raw);
    if (!parsed?.masterAccountId) {
      clearActiveMasterAccountId();
      return null;
    }

    // No token → cannot validate sub. Return id without clearing so
    // callers that only need a zone/correlation hint stay safe.
    if (accessToken == null || accessToken === "") {
      return String(parsed.masterAccountId);
    }

    const sub = subFromToken(accessToken);
    if (!sub || String(parsed.sub) !== String(sub)) {
      clearActiveMasterAccountId();
      return null;
    }
    return String(parsed.masterAccountId);
  } catch {
    return null;
  }
}

/** Best-effort claim extraction from a JWT access token payload. */
export function masterAccountIdFromToken(token) {
  if (!token || typeof token !== "string") return null;
  try {
    const payload = JSON.parse(
      atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")),
    );
    return (
      payload.masterAccountId ||
      payload.impersonatedMasterAccountId ||
      payload["https://krosskast.com/master_account_id"] ||
      payload["https://krosskast.com/masterAccountId"] ||
      null
    );
  } catch {
    return null;
  }
}

/**
 * Resolve active MA id for GET /me/tenant.
 * Priority: Support Mode handoff → sub-scoped persisted → JWT claim.
 */
export function resolveActiveMasterAccountId(accessToken) {
  const session = loadImpersonationSession();
  if (session?.masterAccountId) {
    return String(session.masterAccountId);
  }
  const stored = readStoredActiveMasterAccountId(accessToken);
  if (stored) return stored;
  const fromToken = masterAccountIdFromToken(accessToken);
  if (fromToken) return String(fromToken);
  return null;
}

export { subFromToken };
